import { useRef, useState } from 'react';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import getDomain from '@utils/GetDomain';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { useAuth } from 'react-oidc-context';
import {
  MainMenu,
  useUserAccess,
  SetupMenu,
  LanguageSelector,
  clearAuthTokens,
  useStore,
} from '@ui-framework/index';
import { APP_SUPPORTS_MULTI_LANGUAGE } from '@constants/app';
import Logo, { LOGO_TYPE } from './Logo';
import HeaderUserAvatar from './HeaderUserAvatar';
import IconLibrary from './IconLibrary';
import menu from '../../config/menu';
import {
  ServiceModuleProvider,
  useServiceModule,
} from './ServiceModuleProvider';

function MenuServiceModuleWrapper({ render }) {
  const { moduleSettings } = useServiceModule();
  return render(moduleSettings);
}

function Header({ ...props }) {
  const { signoutRedirect = () => undefined } = useAuth();
  const store = useStore();
  const userAccess = useUserAccess();
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [setupMenuOpen, setSetupMenuOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef();

  const setupMenuRef = useRef();

  const { sx, ...otherProps } = props;

  const handleOpenNavMenu = event => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = event => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const closeSetupMenu = () => {
    setSetupMenuOpen(false);
  };

  const pages = [
    {
      name: 'Dashboard',
      onClick: () => navigate('/dashboard'),
      toolTip: 'Go to Dashboard',
      Icon: IconLibrary.Dashboard,
    },
    {
      name: 'Menu',
      onClick: event => {
        if (menuRef.current?.openMenu) {
          menuRef.current.openMenu(event.currentTarget);
        }
        handleCloseNavMenu();
      },
      toolTip: 'Open Application Menu',
      Icon: IconLibrary.Menu,
    },
  ];

  const settings = [
    {
      name: 'Profile',
      hasPermission: true,
      onClick: () => navigate('/profile'),
      toolTip: 'Go to my profile',
      Icon: IconLibrary.Profile,
    },
    {
      name: 'Setup',
      hasPermission: userAccess.isSA() || userAccess.isAdmin(),
      onClick: event => {
        if (setupMenuRef.current?.openMenu) {
          setupMenuRef.current.openMenu(event.currentTarget);
          setSetupMenuOpen(true);
        }
        handleCloseNavMenu();
      },
      onMenuClose: () => setSetupMenuOpen(false),
      toolTip: 'Setup',
      Icon: IconLibrary.Setup,
    },
    {
      name: 'Service Provider Profile',
      hasPermission: userAccess.isSP(),
      onClick: () =>
        navigate(`/serviceproviders/edit/${userAccess?.company?.id}`),
      toolTip: 'Service Provider Profile',
      Icon: IconLibrary.ServiceProvider,
    },
    {
      name: 'Logout',
      hasPermission: true,
      onClick: async () => {
        await store.clearStoredData();
        await signoutRedirect({
          post_logout_redirect_uri: getDomain(
            process.env.REACT_APP_API_HOST,
            '',
            false,
          ),
        });
        await clearAuthTokens(() => {});
      },
      toolTip: 'Logout of the application',
      Icon: IconLibrary.Logout,
    },
  ];

  const filteredSettings = settings.filter(setting => setting.hasPermission);

  const languageChanged = async (event, language) => {
    // store.domain.masterData.set(masterDataResult);
    store.domain.locale.set(language);
  };

  return (
    <AppBar
      enableColorOnDark
      position="static"
      {...otherProps}
      sx={{ backgroundColor: 'white', color: 'black', ...sx }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Logo
            sx={{
              display: { xs: 'none', md: 'flex' },
              mr: 1,
            }}
            href="/"
            size="medium"
            type={LOGO_TYPE.FULL}
          />
          <Box
            gap={1}
            sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              onClick={handleOpenNavMenu}
              color="inherit">
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              slotProps={{
                paper: {
                  sx: {
                    '& .MuiList-root': {
                      py: 0,
                    },
                    '& .MuiList-root > li': {
                      px: 1,
                      py: 0.5,
                      my: 1,
                      minHeight: '30px',
                    },
                  },
                },
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: 'block', md: 'none' } }}>
              {pages.map(page => (
                <Tooltip arrow key={page.name} title={page.toolTip}>
                  <MenuItem onClick={page.onClick}>
                    <page.Icon sx={{ fontSize: '18px' }} />
                    <Typography sx={{ textAlign: 'center', ml: 0.5 }}>
                      {page.name}
                    </Typography>
                  </MenuItem>
                </Tooltip>
              ))}
            </Menu>
            <Logo
              sx={{
                display: { xs: 'flex', md: 'none' },
                textAlign: 'center',
                mt: 0,
                ml: -1,
              }}
              href="/"
              size="medium"
              type={LOGO_TYPE.COMPACT}
            />
          </Box>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map(page => (
              <Tooltip arrow key={page.name} title={page.toolTip}>
                <Button
                  onClick={page.onClick}
                  startIcon={<page.Icon />}
                  sx={{
                    color: 'black',
                  }}>
                  {page.name.toUpperCase()}
                </Button>
              </Tooltip>
            ))}
          </Box>
          {APP_SUPPORTS_MULTI_LANGUAGE && (
            <Box mr={2}>
              <LanguageSelector
                defaultLanguage={store.domain.locale.language}
                languagesSupported={store.domain.locale.getSupportedLanguages()}
                onChange={(event, language) => languageChanged(event, language)}
              />
            </Box>
          )}
          <Box sx={{ flexGrow: 0 }}>
            <Button onClick={handleOpenUserMenu} sx={{ p: 0 }}>
              <HeaderUserAvatar />
            </Button>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              slotProps={{
                paper: {
                  sx: {
                    '& .MuiList-root': {
                      py: 0,
                    },
                    '& .MuiList-root > li': {
                      px: 1,
                      py: 0.5,
                      my: 1,
                      minHeight: '30px',
                    },
                  },
                },
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}>
              {filteredSettings.map(setting => (
                <MenuItem key={setting.name} onClick={setting.onClick}>
                  {setting.name === 'Setup' && setupMenuOpen && (
                    <ArrowLeftIcon
                      sx={{
                        fontSize: '18px',
                        color: 'primary.main',
                      }}
                    />
                  )}
                  <setting.Icon sx={{ fontSize: '18px' }} />
                  <Typography sx={{ textAlign: 'center', ml: 0.5 }}>
                    {setting.name}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
      <ServiceModuleProvider>
        <MenuServiceModuleWrapper
          render={moduleSettings => {
            return (
              <>
                <MainMenu
                  ref={menuRef}
                  items={menu.menu({
                    navigate,
                    store,
                    userAccess,
                    IconLibrary,
                    moduleSettings,
                  })}
                />
                <SetupMenu
                  ref={setupMenuRef}
                  items={menu.setup({
                    navigate,
                    userAccess,
                    IconLibrary,
                    moduleSettings,
                  })}
                  onClose={() => {
                    setSetupMenuOpen(false);
                    setAnchorElUser(null);
                  }}
                  closeSetupMenu={closeSetupMenu}
                />
              </>
            );
          }}
        />
      </ServiceModuleProvider>
    </AppBar>
  );
}
export default Header;
